/**
 * ReassertManaged — "Re-apply managed settings" for one gateway and for every gateway at a site.
 *
 * WHY: Meridian owns a few config-raw lines on every gateway (tagged-VLAN -> WAN forwarding,
 * the Shasta DNS sinkhole, HA dhcp `force`). Every gateway push re-asserts them, but until this
 * there was no way to push ONLY them: on 2026-10-08 they rode on a no-op guest-isolation edit and
 * the audit log said `firewall.isolation`. This is the honest action — no edit, its own audit
 * name (`device.gateway.config.reassert`), and the exact line diff BEFORE anything is sent.
 *
 * Safety contract mirrored from the server (POST /gateway/reassert, /sites/{id}/gateways/reassert):
 *   - the preview (dry_run) always runs first and writes nothing;
 *   - removals outside the managed blocks are refused unless the operator ticks an explicit box;
 *   - the usual config-push warning: the gateway's APs drop for ~4 minutes and self-recover;
 *   - site run: one gateway at a time, HA secondary (BACKUP) first, stop on the first failure.
 * Role: org_admin (GATEWAY_WRITE_ROLE on the server) — RoleGate disables, never hides.
 */
import { useEffect, useState } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import {
  useApplyGatewayReassert,
  usePlanGatewayReassert,
  usePlanSiteReassert,
  useSiteReassertStatus,
  useStartSiteReassert,
} from "@/lib/api/hooks";
import type { GatewayRawLine, GatewayReassertSummary } from "@/types/domain";
import { Button } from "@/components/ui/Button";
import { ErrorBanner, EmptyState } from "@/components/ui/Page";
import { RoleGate } from "@/components/auth/RoleGate";
import { PlanWarnings } from "@/components/gateway/GatewayChrome";
import { cn } from "@/lib/cn";

/** Must match api/v2/device_gateway.GATEWAY_WRITE_ROLE. */
const REASSERT_ROLE = "org_admin" as const;
const ROLE_REASON = "Re-applying gateway settings requires a customer admin.";

/** Plain-language names for the managed folds (the server's fold ids). */
const FOLD_LABEL: Record<string, string> = {
  vlan_wan_forwarding: "Tagged VLAN → WAN forwarding",
  shasta_sinkhole: "Shasta DNS sinkhole",
  ha_dhcp_force: "HA DHCP force",
};

/** The standing config-push warning (memory: a uCentral push = ~4 min AP outage). Exported so
 *  every gateway push dialog (re-assert, Enable state reporting) shows the same words. */
export function PushDisruptionWarning({ many }: { many?: boolean }) {
  return (
    <div role="note" className="md-card border-warn/40 bg-warn-soft p-3 text-sm text-ink">
      <div className="font-medium">Expect about 4 minutes of disruption{many ? " per gateway" : ""}</div>
      <div className="mt-1 text-ink-muted">
        A configuration push makes the access points behind {many ? "each" : "this"} gateway drop
        offline for roughly 4 minutes before they recover on their own. Wait it out — do not push
        again or reboot while they come back.
      </div>
    </div>
  );
}

/** One uci batch line as the device will run it: `set dhcp.down1v0.force 1`. */
function rawText(line: unknown[]): string {
  return line.map((p) => String(p)).join(" ");
}

/** The line-level diff: + added, − removed, each tagged with the fold that owns it. */
function ManagedLineDiff({ summary }: { summary: GatewayReassertSummary }) {
  const rows: { op: "+" | "−"; item: GatewayRawLine }[] = [
    ...summary.removed.map((item) => ({ op: "−" as const, item })),
    ...summary.added.map((item) => ({ op: "+" as const, item })),
  ];
  return (
    <div className="md-card overflow-hidden">
      <div className="flex flex-wrap items-center gap-2 border-b border-line px-4 py-2 text-xs text-ink-muted">
        <span>
          config-raw: {summary.before_lines} → {summary.after_lines} lines
        </span>
        {Object.entries(summary.folds)
          .filter(([, c]) => c.added || c.removed)
          .map(([fold, c]) => (
            <span key={fold} className="rounded bg-surface-2 px-1.5 py-0.5">
              {FOLD_LABEL[fold] ?? fold}: +{c.added} / −{c.removed}
            </span>
          ))}
        {summary.reordered && <span>(re-ordered only)</span>}
      </div>
      <ul className="max-h-64 overflow-y-auto font-mono text-[0.6875rem]">
        {rows.map(({ op, item }, i) => (
          <li
            key={`${op}-${i}`}
            className={cn(
              "flex gap-2 px-4 py-1",
              item.fold === null && op === "−" && "bg-critical-soft",
            )}
          >
            <span className={cn("font-bold", op === "+" ? "text-healthy" : "text-critical")}>{op}</span>
            <span className="break-all text-ink">{rawText(item.line)}</span>
            <span className="ml-auto shrink-0 text-ink-subtle">
              {item.fold ? FOLD_LABEL[item.fold] : "NOT managed"}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** The explicit opt-in the server requires for a removal outside the managed blocks. */
function ConfirmRemovals({
  count,
  checked,
  onChange,
}: {
  count: number;
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <label className="md-card flex items-start gap-2 border-critical/40 bg-critical-soft p-3 text-sm">
      <input type="checkbox" className="mt-1" checked={checked} onChange={(e) => onChange(e.target.checked)} />
      <span>
        I understand this removes {count} item{count === 1 ? "" : "s"} that Meridian does not manage
        (marked <span className="font-medium">NOT managed</span> above). Push anyway.
      </span>
    </label>
  );
}

/* ------------------------------------------------------------------ *
 * One gateway (device sheet)
 * ------------------------------------------------------------------ */

export function ReassertGatewayButton({ serial }: { serial: string }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <RoleGate min={REASSERT_ROLE} mode="disable" reason={ROLE_REASON}>
        <Button variant="secondary" onClick={() => setOpen(true)}>
          Re-apply managed settings
        </Button>
      </RoleGate>
      {open && <ReassertGatewayDialog serial={serial} onClose={() => setOpen(false)} />}
    </>
  );
}

function ReassertGatewayDialog({ serial, onClose }: { serial: string; onClose: () => void }) {
  const plan = usePlanGatewayReassert(serial);
  const apply = useApplyGatewayReassert(serial);
  const [confirmRemovals, setConfirmRemovals] = useState(false);

  // Preview once on open. Read-only on the server.
  const { mutate: runPlan } = plan;
  useEffect(() => {
    runPlan();
  }, [runPlan]);

  const planned = plan.data;
  const needsConfirm = !!planned?.reassert.requires_confirmation;
  const done = apply.data;

  return (
    <Dialog.Root open onOpenChange={(o) => !o && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-sheet bg-ink/40" />
        <Dialog.Content className="md-dialog fixed left-1/2 top-1/2 z-sheet max-h-[90vh] w-full max-w-2xl -translate-x-1/2 -translate-y-1/2 overflow-y-auto rounded-lg border border-line bg-overlay p-5 shadow-sheet outline-none">
          <Dialog.Title className="text-lg font-semibold text-ink">Re-apply managed settings</Dialog.Title>
          <Dialog.Description className="mt-1 text-sm text-ink-muted">
            Pushes only the settings Meridian manages on {serial} (VLAN forwarding, Shasta sinkhole, HA
            DHCP force). Nothing has been sent yet — this is the exact change.
          </Dialog.Description>

          {plan.isPending && <div className="mt-4 text-sm text-ink-muted">Working out what would change…</div>}
          {plan.isError && (
            <div className="mt-4">
              <ErrorBanner error={plan.error} />
            </div>
          )}

          {planned && !done && (
            <div className="mt-4 flex flex-col gap-3">
              {!planned.changed ? (
                <EmptyState
                  title="Already up to date"
                  hint="The gateway already carries every managed setting, so there is nothing to push."
                />
              ) : (
                <>
                  <PlanWarnings warnings={planned.warnings} />
                  <ManagedLineDiff summary={planned.reassert} />
                  <PushDisruptionWarning />
                  {needsConfirm && (
                    <ConfirmRemovals
                      count={planned.reassert.unexpected.length}
                      checked={confirmRemovals}
                      onChange={setConfirmRemovals}
                    />
                  )}
                </>
              )}
              {apply.isError && <ErrorBanner error={apply.error} />}
              <div className="flex flex-wrap justify-end gap-2">
                <Button variant="ghost" onClick={onClose} disabled={apply.isPending}>
                  Cancel
                </Button>
                <RoleGate min={REASSERT_ROLE} mode="disable" reason={ROLE_REASON}>
                  <Button
                    variant="danger"
                    disabled={!planned.changed || apply.isPending || (needsConfirm && !confirmRemovals)}
                    onClick={() =>
                      apply.mutate({ plan_token: planned.plan_token, confirm_removals: confirmRemovals })
                    }
                  >
                    {apply.isPending ? "Pushing…" : "Re-apply — push to gateway"}
                  </Button>
                </RoleGate>
              </div>
            </div>
          )}

          {done && (
            <div className="mt-4 flex flex-col gap-3">
              <div className="md-card border-healthy/40 bg-healthy-soft p-4 text-sm">
                <div className="font-medium text-ink">
                  {done.changed ? "Pushed to the gateway" : "Nothing to push — already applied"}
                </div>
                <div className="mt-1 text-ink-muted">
                  {done.changed
                    ? "owgw accepted the push. These lines are not reported back in device state, so the read-back can confirm the new configuration was picked up, not each line. APs recover in about 4 minutes."
                    : "The gateway already carried every managed setting."}
                </div>
              </div>
              <div className="flex justify-end">
                <Button variant="primary" onClick={onClose}>
                  Done
                </Button>
              </div>
            </div>
          )}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

/* ------------------------------------------------------------------ *
 * Every gateway at a site (Gateway > VLANs)
 * ------------------------------------------------------------------ */

const ROLE_TEXT: Record<string, string> = {
  secondary: "HA backup — goes first",
  primary: "HA primary",
  single: "gateway",
};

export function SiteReassertCard({ siteId }: { siteId: string }) {
  const status = useSiteReassertStatus(siteId);
  const [open, setOpen] = useState(false);
  const run = status.data?.run ?? null;
  const running = run?.status === "running";

  return (
    <section className="md-card p-4">
      <div className="flex flex-wrap items-center gap-2">
        <div>
          <div className="font-medium text-ink">Re-apply managed settings on every gateway</div>
          <div className="text-xs text-ink-muted">
            One gateway at a time; on an HA pair the backup goes first. Each waits until the gateway reports
            the new configuration, and the run stops at the first failure.
          </div>
        </div>
        <div className="ml-auto">
          <RoleGate min={REASSERT_ROLE} mode="disable" reason={ROLE_REASON}>
            <Button variant="secondary" disabled={running} onClick={() => setOpen(true)}>
              {running ? "Running…" : "Preview re-apply"}
            </Button>
          </RoleGate>
        </div>
      </div>
      {status.error && <ErrorBanner error={status.error} />}
      {run && (
        <div className="mt-3 text-sm">
          <div className="text-ink-muted">
            Last run: <span className="font-medium text-ink">{run.status}</span>
            {run.started_by ? ` · by ${run.started_by}` : ""}
            {run.error ? ` — ${run.error}` : ""}
          </div>
          <ul className="mt-1 flex flex-col gap-0.5 text-xs">
            {run.gateways.map((g) => (
              <li key={g.serial} className="flex gap-2">
                <span className="font-medium text-ink">{g.name ?? g.serial}</span>
                <span className="text-ink-subtle">{ROLE_TEXT[g.role ?? "single"] ?? g.role}</span>
                <span
                  className={cn(
                    "ml-auto",
                    g.status === "failed" ? "text-critical" : g.status === "applied" ? "text-healthy" : "text-ink-muted",
                  )}
                >
                  {g.status}
                  {g.error ? `: ${g.error}` : ""}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
      {open && <SiteReassertDialog siteId={siteId} onClose={() => setOpen(false)} />}
    </section>
  );
}

function SiteReassertDialog({ siteId, onClose }: { siteId: string; onClose: () => void }) {
  const plan = usePlanSiteReassert(siteId);
  const start = useStartSiteReassert(siteId);
  const [confirmRemovals, setConfirmRemovals] = useState(false);

  const { mutate: runPlan } = plan;
  useEffect(() => {
    runPlan();
  }, [runPlan]);

  const planned = plan.data;
  const unexpected = planned?.gateways.reduce((n, g) => n + (g.reassert?.unexpected.length ?? 0), 0) ?? 0;

  return (
    <Dialog.Root open onOpenChange={(o) => !o && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-sheet bg-ink/40" />
        <Dialog.Content className="md-dialog fixed left-1/2 top-1/2 z-sheet max-h-[90vh] w-full max-w-2xl -translate-x-1/2 -translate-y-1/2 overflow-y-auto rounded-lg border border-line bg-overlay p-5 shadow-sheet outline-none">
          <Dialog.Title className="text-lg font-semibold text-ink">Re-apply managed settings — whole site</Dialog.Title>
          <Dialog.Description className="mt-1 text-sm text-ink-muted">
            Nothing has been sent yet. Gateways are listed in the order they would be pushed.
          </Dialog.Description>

          {plan.isPending && <div className="mt-4 text-sm text-ink-muted">Previewing every gateway…</div>}
          {plan.isError && (
            <div className="mt-4">
              <ErrorBanner error={plan.error} />
            </div>
          )}

          {planned && !start.isSuccess && (
            <div className="mt-4 flex flex-col gap-3">
              {planned.refusals.length > 0 && (
                <PlanWarnings warnings={planned.refusals.map((r) => `Refused: ${r.message}`)} />
              )}
              <ol className="flex flex-col gap-3">
                {planned.gateways.map((g, i) => (
                  <li key={g.serial} className="flex flex-col gap-2">
                    <div className="text-sm">
                      <span className="font-medium text-ink">
                        {i + 1}. {g.name}
                      </span>{" "}
                      <span className="text-ink-subtle">{ROLE_TEXT[g.role] ?? g.role}</span>
                      {!g.changed && !g.error && <span className="ml-2 text-ink-muted">— already up to date</span>}
                    </div>
                    {g.changed && g.reassert && (
                      <>
                        <PlanWarnings warnings={g.warnings} />
                        <ManagedLineDiff summary={g.reassert} />
                      </>
                    )}
                  </li>
                ))}
              </ol>
              {planned.can_apply && <PushDisruptionWarning many />}
              {planned.requires_confirmation && (
                <ConfirmRemovals count={unexpected} checked={confirmRemovals} onChange={setConfirmRemovals} />
              )}
              {start.isError && <ErrorBanner error={start.error} />}
              <div className="flex flex-wrap justify-end gap-2">
                <Button variant="ghost" onClick={onClose} disabled={start.isPending}>
                  Cancel
                </Button>
                <RoleGate min={REASSERT_ROLE} mode="disable" reason={ROLE_REASON}>
                  <Button
                    variant="danger"
                    disabled={
                      !planned.can_apply ||
                      start.isPending ||
                      (planned.requires_confirmation && !confirmRemovals)
                    }
                    onClick={() =>
                      start.mutate({ plan_token: planned.plan_token, confirm_removals: confirmRemovals })
                    }
                  >
                    {start.isPending ? "Starting…" : "Re-apply on every gateway"}
                  </Button>
                </RoleGate>
              </div>
            </div>
          )}

          {start.isSuccess && (
            <div className="mt-4 flex flex-col gap-3">
              <div className="md-card border-healthy/40 bg-healthy-soft p-4 text-sm">
                <div className="font-medium text-ink">Started</div>
                <div className="mt-1 text-ink-muted">
                  The run continues in the background; progress shows on the card. Each gateway takes several
                  minutes.
                </div>
              </div>
              <div className="flex justify-end">
                <Button variant="primary" onClick={onClose}>
                  Close
                </Button>
              </div>
            </div>
          )}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
